import React, { useEffect, useState } from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, GroupAvatar, Icon, SearchBar, Switch, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import {
  DISCOVERY_RADII,
  defaultDiscoveryFilters,
  discoveryNotice,
  eventPriceLabel,
  formatDistanceKm,
  type DiscoveryFilters,
} from '@utils'
import { useDiscoverEvents, type DiscoverEvent } from '@hooks'
import { OptionChips } from './OptionChips'

const SEARCH_DELAY_MS = 350

const localeOf = (language: string) =>
  language === 'en' ? 'en-GB' : language === 'es' ? 'es-ES' : 'fr-FR'

/** "Découvrir": public upcoming events, searchable, optionally around me. */
export function DiscoverEvents({ onOpen }: { onOpen: (code: string) => void }) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [filters, setFilters] = useState<DiscoveryFilters>(defaultDiscoveryFilters)
  const [typed, setTyped] = useState('')
  const query = useDiscoverEvents(filters)

  // Wait for a pause in typing before asking the server.
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((current) => ({ ...current, search: typed })),
      SEARCH_DELAY_MS,
    )
    return () => clearTimeout(timer)
  }, [typed])

  const events = query.data?.pages.flatMap((page) => page.data) ?? []
  const first = query.data?.pages[0]?.metadata
  const notice = discoveryNotice(first?.near)
  const searching = filters.search.trim().length > 0
  // "Près de moi" really narrowed the list (a position or a city is known).
  const aroundMe = filters.nearMe && notice?.key !== 'locUnknown'

  return (
    <View style={styles.wrapper} testID="discover-events">
      <SearchBar
        value={typed}
        onChangeText={setTyped}
        placeholder={t('events.discover.searchPlaceholder')}
        accessibilityLabel={t('events.discover.searchLabel')}
        testID="discover-search"
      />
      <View style={styles.filters}>
        <View style={styles.nearSwitch}>
          <Switch
            left={{ label: t('events.discover.allEvents'), value: 'all', icon: 'globe' }}
            right={{ label: t('events.discover.nearMe'), value: 'near', icon: 'mapPin' }}
            value={filters.nearMe ? 'near' : 'all'}
            onChange={(value) => setFilters((current) => ({ ...current, nearMe: value === 'near' }))}
          />
        </View>
        {filters.nearMe ? (
          <OptionChips
            label={t('events.discover.radiusLabel')}
            testID="discover-radius"
            options={DISCOVERY_RADII.map((km) => ({
              value: String(km),
              label: t('events.discover.radius', { km }),
            }))}
            value={String(filters.radiusKm)}
            // Tapping the selected chip would clear it: a radius is always kept.
            onChange={(value) =>
              value &&
              setFilters((current) => ({
                ...current,
                radiusKm: Number(value) as DiscoveryFilters['radiusKm'],
              }))
            }
          />
        ) : null}
      </View>
      {notice ? (
        <View style={styles.notice} testID={`discover-notice-${notice.key}`}>
          <Icon name="mapPin" size={16} color={theme.colors.primary} />
          <Text type="bodySmall" color={theme.colors.secondary} style={styles.noticeText}>
            {t(`events.discover.${notice.key}`, { km: notice.km ?? '' })}
          </Text>
        </View>
      ) : null}

      {query.isLoading ? (
        <ActivityIndicator color={theme.colors.primary} testID="discover-loading" />
      ) : query.isError ? (
        <View style={styles.state} testID="discover-error">
          <Text type="body" center color={theme.colors.error}>
            {t('events.discover.error')}
          </Text>
          <Button title={t('events.discover.retry')} isSmall onPress={() => query.refetch()} />
        </View>
      ) : events.length === 0 ? (
        <View style={styles.state} testID="discover-empty">
          <Icon name="calendarStar" size={48} color={theme.colors.primary} />
          <Text type="subtitleBold" center>
            {aroundMe
              ? t('events.discover.emptyNear')
              : searching
                ? t('events.discover.emptySearch')
                : t('events.discover.empty')}
          </Text>
          <Text type="body" center color={theme.colors.greyBlue}>
            {aroundMe
              ? t('events.discover.emptyNearHint')
              : t('events.discover.emptyHint')}
          </Text>
        </View>
      ) : (
        <>
          <Text type="bodySmall" color={theme.colors.greyBlue} testID="discover-count">
            {t('events.discover.resultsCount', { count: first?.count ?? events.length })}
          </Text>
          {events.map((event) => (
            <DiscoverCard key={event.id} event={event} onPress={() => onOpen(event.code)} />
          ))}
          {query.hasNextPage ? (
            <View style={styles.more}>
              <Button
                title={query.isFetchingNextPage ? t('events.discover.loading') : t('events.discover.loadMore')}
                unfilled
                isSmall
                disabled={query.isFetchingNextPage}
                onPress={() => query.fetchNextPage()}
                testID="discover-load-more"
              />
            </View>
          ) : null}
        </>
      )}
    </View>
  )
}

function DiscoverCard({ event, onPress }: { event: DiscoverEvent; onPress: () => void }) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const locale = localeOf(currentLanguage)
  const start = new Date(event.startsAt)
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(start).replace('.', '')
  const when = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(start)
  const place =
    event.format === 'online'
      ? t('events.discover.online')
      : // The venue often already ends with the city ("Station F, Paris").
        [event.location, event.city]
          .filter((part, index, parts): part is string =>
            Boolean(part) && (index === 0 || !parts[0]?.toLowerCase().includes(part!.toLowerCase())),
          )
          .join(', ')
  const going = event.contactsGoing

  return (
    <TouchableOpacity
      accessibilityRole="button"
      onPress={onPress}
      style={styles.card}
      testID={`discover-card-${event.id}`}
    >
      <View style={styles.dateBlock}>
        <Text type="bodyExtraSmallBold" color={theme.colors.white}>
          {month.toUpperCase()}
        </Text>
        <Text type="titleBigBold" color={theme.colors.white}>
          {String(start.getDate())}
        </Text>
      </View>
      <View style={styles.body}>
        <Text type="subtitleBold" numberOfLines={2}>
          {event.name}
        </Text>
        <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
          {[when, place].filter(Boolean).join(' · ')}
        </Text>
        <View style={styles.chips}>
          {event.category ? (
            <Chip label={t(`events.category.${event.category}`)} color={theme.colors.secondary} />
          ) : null}
          <Chip
            label={
              event.capacity !== null
                ? t('events.attendeesCountWithCapacity', {
                    count: event.attendeeCount,
                    capacity: event.capacity,
                  })
                : t('events.attendeesCount', { count: event.attendeeCount })
            }
            color={theme.colors.secondary}
          />
          {event.priceCents !== null ? (
            <Chip label={eventPriceLabel(event, '', currentLanguage)} color={theme.colors.primary} />
          ) : null}
          {event.distanceKm !== null ? (
            <Chip
              label={t('events.discover.distance', {
                km: formatDistanceKm(event.distanceKm, locale),
              })}
              color={theme.colors.secondary}
            />
          ) : null}
          {event.attending ? (
            <Chip label={t('events.discover.registered')} color={theme.colors.green} />
          ) : null}
          {event.isFull ? <Chip label={t('events.full')} color={theme.colors.error} /> : null}
        </View>
        {going.count > 0 ? (
          <View style={styles.going} testID={`discover-going-${event.id}`}>
            <GroupAvatar
              profiles={going.preview.map((p) => ({
                id: p.accountId,
                avatarUrl: p.avatarUrl ?? undefined,
              }))}
            />
            <Text type="bodySmallBold" color={theme.colors.primary} style={styles.goingText}>
              {t('events.discover.contactsGoing', { count: going.count })}
            </Text>
          </View>
        ) : null}
      </View>
      <Icon name="arrowRight" size={18} color={theme.colors.greyBlue} />
    </TouchableOpacity>
  )
}

function Chip({ label, color }: { label: string; color: string }) {
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <View style={[styles.chip, { borderColor: color === theme.colors.secondary ? theme.colors.grey : color }]}>
      <Text type="bodyExtraSmallBold" color={color}>
        {label}
      </Text>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    wrapper: { width: '100%', gap: theme.spacing.md },
    filters: { gap: theme.spacing.md },
    nearSwitch: { maxWidth: 360, width: '100%' },
    notice: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: theme.spacing.sm,
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.grey05,
    },
    noticeText: { flex: 1, minWidth: 0 },
    state: { alignItems: 'center', gap: theme.spacing.md, paddingVertical: theme.spacing.xxxl },
    more: { alignItems: 'center', paddingVertical: theme.spacing.md },
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.md,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    dateBlock: {
      width: 56,
      height: 62,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    body: { flex: 1, minWidth: 0, gap: 2 },
    chips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
      marginTop: theme.spacing.xs,
    },
    chip: {
      borderWidth: 1,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: 2,
    },
    going: { flexDirection: 'row', alignItems: 'center', marginTop: theme.spacing.xs },
    goingText: { flexShrink: 1 },
  })
